import React, { useEffect, useState } from 'react';
import {
  getPeriods,
  addPeriods,
  deletePeriods
} from '../../services/timeOverviewAPIs';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Box,
  Typography,
  Grid,
  Paper,
  useTheme,
  IconButton,
  Chip
} from '@mui/material';
import { moroccanHolidays2026, getHolidayInfo } from '../../utils/moroccanHolidays';

const IsEmployee = localStorage.getItem('userRole') === 'EMPLOYEE';

const YearCalendar = ({ calendarData, onDayClick, year, setYear, selectedDates }) => {
  const theme = useTheme();
  const months = [...Array(12).keys()];

  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: theme.palette.mode === 'dark' ? '#0f172a' : 'background.default',
      p: { xs: 1, sm: 2, md: 3 }
    }}>
      <Box sx={{
        width: '100%',
        mx: 'auto',
        bgcolor: theme.palette.mode === 'dark' ? '#1e293b' : 'background.paper',
        borderRadius: 4,
        p: { xs: 2, sm: 3, md: 4 },
        boxShadow: theme.palette.mode === 'dark' ? '0 8px 32px rgba(0,0,0,0.4)' : 3,
        border: 1,
        borderColor: theme.palette.mode === 'dark' ? '#334155' : 'divider',
        overflowX: 'hidden'
      }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h3" component="h1" fontWeight="800" gutterBottom color={theme.palette.mode === 'dark' ? 'white' : 'text.primary'} sx={{ letterSpacing: '-0.5px' }}>
            {year} Calendar
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Track your schedule throughout the year
          </Typography>
        </Box>

        {/* Legend */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          mb: 4,
          p: 2,
          borderRadius: 3,
          bgcolor: theme.palette.mode === 'dark' ? '#0f172a' : '#f8fafc',
          border: 1,
          borderColor: theme.palette.mode === 'dark' ? '#334155' : 'divider'
        }}>
          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 3,
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {[
              { label: 'Available', color: theme.palette.mode === 'dark' ? '#1e293b' : '#ffffff', border: theme.palette.mode === 'dark' ? '#475569' : '#cbd5e1' },
              { label: 'Weekends', color: theme.palette.mode === 'dark' ? '#2563eb' : '#e0f2fe', border: theme.palette.mode === 'dark' ? '#3b82f6' : '#bae6fd' },
              { label: 'Recommended', color: theme.palette.mode === 'dark' ? '#059669' : '#dcfce7', border: theme.palette.mode === 'dark' ? '#10b981' : '#86efac' },
              { label: 'Blocked', color: theme.palette.mode === 'dark' ? '#dc2626' : '#fee2e2', border: theme.palette.mode === 'dark' ? '#ef4444' : '#fca5a5' },
              { label: 'Islamic Holiday', gradient: 'linear-gradient(135deg, #059669 0%, #10b981 100%)', border: '#10b981' }
            ].map((item, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '4px',
                    bgcolor: item.color,
                    background: item.gradient,
                    border: `1px solid ${item.border}`,
                    boxShadow: theme.palette.mode === 'dark' ? 'inset 0 2px 4px rgba(255,255,255,0.05)' : 'none'
                  }}
                />
                <Typography variant="caption" fontWeight="600" color={theme.palette.mode === 'dark' ? '#cbd5e1' : 'text.primary'}>
                  {item.label}
                </Typography>
              </Box>
            ))}

            <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
              <select
                className="axo-filter-select"
                style={{ minWidth: '110px' }}
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
              >
                {[2023, 2024, 2025, 2026, 2027].map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </Box>
          </Box>
        </Box>

        {/* Months Grid */}
        <Box sx={{ width: '100%', overflowX: 'auto', pb: 2, display: 'flex', justifyContent: 'center' }}>
          <Box sx={{ minWidth: { lg: '1500px', xl: '100%' }, display: 'flex', justifyContent: 'center' }}>
            <Grid container spacing={2} alignItems="stretch" justifyContent="center">
              {months.map(month => (
                <Grid item xs={12} sm={6} md={4} lg={2} xl={2} key={month} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                    <MonthView
                      month={month}
                      year={year}
                      data={calendarData}
                      onDayClick={onDayClick}
                      selectedDates={selectedDates}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const MonthView = ({ month, year, data, onDayClick, selectedDates = [] }) => {
  const theme = useTheme();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const monthName = new Date(year, month).toLocaleString('default', { month: 'long' });
  const dayNames = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 4,
        border: 1,
        borderColor: theme.palette.mode === 'dark' ? '#334155' : 'divider',
        bgcolor: theme.palette.mode === 'dark' ? '#0f172a' : '#f8fafc',
        height: '350px',
        width: '350px',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: theme.palette.mode === 'dark' ? '0 8px 24px rgba(0,0,0,0.6)' : 6,
          borderColor: theme.palette.mode === 'dark' ? '#3b82f6' : 'primary.main',
          transform: 'translateY(-4px)'
        }
      }}
    >
      <Typography variant="h6" fontWeight="800" align="center" gutterBottom color={theme.palette.mode === 'dark' ? '#60a5fa' : 'primary.main'} sx={{ textTransform: 'uppercase', letterSpacing: '1px', mb: 2 }}>
        {monthName}
      </Typography>

      {/* Day headers */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, mb: 1 }}>
        {dayNames.map((day, index) => {
          const isWeekendHeader = index === 0 || index === 6; // Su or Sa
          return (
            <Typography key={day} variant="caption" align="center" fontWeight="bold" sx={{
              color: isWeekendHeader
                ? (theme.palette.mode === 'dark' ? '#60a5fa' : '#0284c7')
                : (theme.palette.mode === 'dark' ? '#94a3b8' : 'text.secondary'),
              fontSize: '0.75rem',
              textTransform: 'uppercase'
            }}>
              {day}
            </Typography>
          );
        })}
      </Box>

      {/* Calendar grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
        {/* Empty cells */}
        {[...Array(firstDayOfMonth)].map((_, i) => (
          <Box key={`empty-${i}`} />
        ))}

        {/* Days */}
        {[...Array(daysInMonth)].map((_, i) => {
          const day = i + 1;
          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

          const date = new Date(year, month, day);
          const isWeekend = date.getDay() === 0 || date.getDay() === 6;

          const dayData = data[dateStr] || {};
          let status = typeof dayData === 'string' ? dayData : dayData.status || 'AVAILABLE';
          const title = typeof dayData === 'object' ? dayData.title || '' : '';
          const description = typeof dayData === 'object' ? dayData.description || '' : '';

          const holidayInfo = getHolidayInfo(day, month, year);
          const isHoliday = !!holidayInfo && holidayInfo.type !== 'national';

          let bgcolor = theme.palette.mode === 'dark' ? '#1e293b' : 'white';
          let borderColor = theme.palette.mode === 'dark' ? '#334155' : '#e2e8f0';
          let color = theme.palette.mode === 'dark' ? '#e2e8f0' : '#334155';
          let hoverBg = theme.palette.mode === 'dark' ? '#334155' : '#f1f5f9';

          if (isWeekend) {
            bgcolor = theme.palette.mode === 'dark' ? '#2563eb' : '#e0f2fe';
            borderColor = theme.palette.mode === 'dark' ? '#3b82f6' : '#bae6fd';
            color = theme.palette.mode === 'dark' ? '#ffffff' : '#0369a1';
          }

          if (status === "RECOMMENDED") {
            bgcolor = theme.palette.mode === 'dark' ? '#059669' : '#dcfce7';
            borderColor = theme.palette.mode === 'dark' ? '#10b981' : '#86efac';
            color = theme.palette.mode === 'dark' ? '#ffffff' : '#166534';
          } else if (status === "BLOCKED") {
            bgcolor = theme.palette.mode === 'dark' ? '#dc2626' : '#fee2e2';
            borderColor = theme.palette.mode === 'dark' ? '#ef4444' : '#fca5a5';
            color = theme.palette.mode === 'dark' ? '#ffffff' : '#991b1b';
          }

          let background = bgcolor;

          if (isHoliday) {
            color = 'white';
            if (holidayInfo.type === 'islamic') {
              background = 'linear-gradient(135deg, #059669 0%, #10b981 100%)';
              borderColor = '#10b981';
            } else if (holidayInfo.type === 'national') {
              background = 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)';
              borderColor = '#ef4444';
            }
          }

          const isSelected = selectedDates.includes(dateStr);

          return (
            <Box
              key={day}
              onClick={() => onDayClick(dateStr, { status, title, description })}
              title={`${monthName} ${day}, ${year}${isHoliday ? ` - ${holidayInfo.name} 🎉` : ` - ${status || 'Available'}`}${title ? `: ${title}` : ''}`}
              sx={{
                aspectRatio: '1', // makes it square
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 1.5,
                userSelect: 'none',
                fontSize: '0.95rem',
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                bgcolor: !background.includes('gradient') ? bgcolor : undefined,
                background: background.includes('gradient') ? background : undefined,
                border: `1px solid ${borderColor}`,
                color: color,
                outline: isSelected ? `2px solid ${theme.palette.mode === 'dark' ? '#60a5fa' : theme.palette.primary.main}` : 'none',
                outlineOffset: 2,
                boxShadow: isSelected ? (theme.palette.mode === 'dark' ? '0 0 10px rgba(96, 165, 250, 0.5)' : '0 0 10px rgba(0,0,0,0.2)') : 'none',
                opacity: isWeekend && !isHoliday && status === 'AVAILABLE' ? 0.9 : 1,
                '&:hover': {
                  bgcolor: !background.includes('gradient') ? hoverBg : undefined,
                  transform: 'scale(1.15)',
                  zIndex: 10,
                  boxShadow: theme.palette.mode === 'dark' ? '0 4px 12px rgba(0,0,0,0.8)' : '0 4px 12px rgba(0,0,0,0.15)'
                }
              }}
            >
              {day}
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

const App = () => {
  const theme = useTheme();
  const [calendarData, setCalendarData] = useState({});
  const [selectedDates, setSelectedDates] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState('');

  // For dialog content
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [statusType, setStatusType] = useState('');
  const [isWeekendDay, setIsWeekendDay] = useState(false);

  useEffect(() => {
    fetchCalendar();
  }, [year]);

  const fetchCalendar = async () => {
    try {
      const data = await getPeriods(year);
      setCalendarData(data);
    } catch (error) {
      console.error("Failed to fetch calendar data", error);
    }
  };

  const handleDayClick = (dateStr, dayInfo) => {
    const [yearStr, monthStr, dayStr] = dateStr.split('-').map(Number);
    const date = new Date(yearStr, monthStr - 1, dayStr);
    const isWeekend = date.getDay() === 0 || date.getDay() === 6;

    if (IsEmployee) {
      // For employee: just show info for clicked day
      setSelectedDates([dateStr]); // Single view, but reuse bulk dialog logic
      setTitle(dayInfo.title || '');
      setDescription(dayInfo.description || '');
      setStatusType(dayInfo.status === 'AVAILABLE' ? '' : dayInfo.status || '');
      setIsWeekendDay(isWeekend);
      setDialogOpen(true);
    } else {
      // Admin: toggle selection
      setSelectedDates(prev =>
        prev.includes(dateStr)
          ? prev.filter(d => d !== dateStr)
          : [...prev, dateStr]
      );
    }
  };

  const openBulkDialog = () => {
    if (selectedDates.length === 0) return;
    // Pre-fill only if all selected dates have same title/desc (optional)
    setTitle('');
    setDescription('');
    setStatusType('');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
  };

  const handleSaveBulk = async () => {
    if (selectedDates.length === 0) {
      setSnackbarMsg('No dates selected.');
      return;
    }
    if (!statusType) {
      try {
        await deletePeriods({ dates: selectedDates });
        setSnackbarMsg('Period(s) set to Available successfully');
        fetchCalendar();
        setSelectedDates([]);
      } catch (err) {
        setSnackbarMsg('Failed to set to Available');
      }
      setDialogOpen(false);
      return;
    }
    if (!title.trim()) {
      setSnackbarMsg('Title is required.');
      return;
    }

    const payload = {
      title: title,
      description: description,
      periods: selectedDates.reduce((acc, date) => {
        acc[date] = statusType; // only the status string
        return acc;
      }, {})
    };

    try {
      await addPeriods(payload);
      setSnackbarMsg('Period(s) saved successfully');
      fetchCalendar();
      setSelectedDates([]);
    } catch (err) {
      setSnackbarMsg('Failed to save');
    }
    setDialogOpen(false);
  };

  const handleDeleteBulk = async () => {
    if (selectedDates.length === 0) return;
    try {
      await deletePeriods({ dates: selectedDates });
      setSnackbarMsg('Period(s) deleted');
      fetchCalendar();
      setSelectedDates([]);
    } catch (err) {
      setSnackbarMsg('Failed to delete');
    }
    setDialogOpen(false);
  };

  return (
    <>
      <Snackbar
        open={!!snackbarMsg}
        autoHideDuration={3000}
        message={snackbarMsg}
        onClose={() => setSnackbarMsg('')}
      />

      <YearCalendar
        calendarData={calendarData}
        onDayClick={handleDayClick}
        year={year}
        setYear={setYear}
        selectedDates={selectedDates}
      />

      {/* Show bulk edit button only for admins with selections */}
      {!IsEmployee && selectedDates.length > 0 && (
        <Paper
          elevation={4}
          sx={{
            position: 'fixed',
            bottom: 30,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
            p: 2,
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Typography variant="body2" fontWeight="bold">
            {selectedDates.length} days selected
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={openBulkDialog}
            size="small"
          >
            Edit Selected
          </Button>
          <IconButton size="small" onClick={() => setSelectedDates([])} color="error">
            <Box component="span" sx={{ fontSize: '1.2rem', lineHeight: 1 }}>×</Box>
          </IconButton>
        </Paper>
      )}

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: theme.palette.mode === 'dark' ? '#1e293b' : 'background.paper',
            backgroundImage: 'none'
          }
        }}
      >
        {IsEmployee ? (
          <>
            <DialogTitle sx={{ color: theme.palette.mode === 'dark' ? 'white' : 'inherit' }}>Leave Period Information</DialogTitle>
            <DialogContent dividers sx={{ borderColor: theme.palette.mode === 'dark' ? '#334155' : 'rgba(0, 0, 0, 0.12)' }}>
              {title && (
                <Typography variant="h6" color={theme.palette.mode === 'dark' ? '#60a5fa' : 'primary'} gutterBottom>
                  {title}
                </Typography>
              )}

              {description && (
                <Typography variant="body2" fontStyle="italic" paragraph>
                  {description}
                </Typography>
              )}

              {isWeekendDay && (
                <Typography variant="body1" sx={{ color: theme.palette.mode === 'dark' ? '#bae6fd' : 'inherit' }} paragraph><strong>🎉 It's a weekend!</strong></Typography>
              )}

              {statusType === 'RECOMMENDED' && (
                <Typography variant="body1" sx={{ color: theme.palette.mode === 'dark' ? '#86efac' : 'inherit' }} paragraph>✨ This period is highly recommended for taking leave — a perfect time to relax and recharge 😊!</Typography>
              )}

              {statusType === 'BLOCKED' && (
                <Typography variant="body1" color="error" paragraph>🚫 This period is currently unrecommended for taking leave.</Typography>
              )}

              {(statusType === 'AVAILABLE' || !statusType) && !isWeekendDay && (
                <Typography variant="body1" sx={{ color: theme.palette.mode === 'dark' ? '#e2e8f0' : 'inherit' }} paragraph>✅ This period is open and available for leave requests. Go ahead and plan your time off!</Typography>
              )}

              {(statusType === 'AVAILABLE' || !statusType) && isWeekendDay && (
                <Typography variant="body1" sx={{ color: theme.palette.mode === 'dark' ? '#e2e8f0' : 'inherit' }} paragraph>😊 Have a wonderful weekend! 🌞</Typography>
              )}
            </DialogContent>
            <DialogActions sx={{ borderTop: 1, borderColor: theme.palette.mode === 'dark' ? '#334155' : 'rgba(0, 0, 0, 0.12)' }}>
              <Button onClick={closeDialog} color="primary">
                Close
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle sx={{ color: theme.palette.mode === 'dark' ? 'white' : 'inherit' }}>
              Edit {selectedDates.length} Period(s)
            </DialogTitle>
            <DialogContent dividers sx={{ borderColor: theme.palette.mode === 'dark' ? '#334155' : 'rgba(0, 0, 0, 0.12)' }}>
              <TextField
                label="Title"
                fullWidth
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                margin="normal"
                variant="outlined"
                disabled={!statusType}
                InputProps={{
                  sx: { color: theme.palette.mode === 'dark' ? 'white' : 'inherit' }
                }}
                InputLabelProps={{
                  sx: { color: theme.palette.mode === 'dark' ? '#94a3b8' : 'inherit' }
                }}
              />
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                margin="normal"
                variant="outlined"
                disabled={!statusType}
                InputProps={{
                  sx: { color: theme.palette.mode === 'dark' ? 'white' : 'inherit' }
                }}
                InputLabelProps={{
                  sx: { color: theme.palette.mode === 'dark' ? '#94a3b8' : 'inherit' }
                }}
              />
              <Select
                fullWidth
                value={statusType}
                onChange={(e) => setStatusType(e.target.value)}
                displayEmpty
                sx={{
                  mt: 2,
                  color: theme.palette.mode === 'dark' ? 'white' : 'inherit',
                  '.MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.mode === 'dark' ? '#475569' : 'rgba(0, 0, 0, 0.23)'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.mode === 'dark' ? '#94a3b8' : 'rgba(0, 0, 0, 0.87)'
                  },
                  '.MuiSvgIcon-root': {
                    color: theme.palette.mode === 'dark' ? '#94a3b8' : 'rgba(0, 0, 0, 0.54)'
                  }
                }}
              >
                <MenuItem value="">Available</MenuItem>
                <MenuItem value="RECOMMENDED">Recommended</MenuItem>
                <MenuItem value="BLOCKED">Blocked</MenuItem>
              </Select>
            </DialogContent>
            <DialogActions sx={{ borderTop: 1, borderColor: theme.palette.mode === 'dark' ? '#334155' : 'rgba(0, 0, 0, 0.12)' }}>
              <Button onClick={handleDeleteBulk} color="error">
                Delete All
              </Button>
              <Button onClick={handleSaveBulk} color="primary" variant="contained">
                Save
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default App;